import { useMemberPostStore } from '~/stores/memberPosts';
import { DEBUG, GLOBAL_MESSAGE_EVENT, type PayloadDataType } from './index';
import { useCommentsStore } from '~/stores/comments';
import _ from 'lodash';
import { useUserStore } from '~/stores/auth';
import { handleErrorAndNotificationHandler } from '../errorNotificationHandler';

type UpdatePost = {
  eventType:
    | 'ADD_COMMENT'
    | 'ADD_DRAFT'
    | 'COMPLETED_COMPLIANCE_ACTION'
    | 'DELETE_COMMENT'
    | 'DELETE_DRAFT'
    | 'EDIT_COMMENT'
    | 'EDIT_COMPLIANCE'
    | 'EDIT_SHARING'
    | 'EDIT_CLASSIFICATION'
    | 'CREATE_COMMENT'
    | 'PUBLISH_POST'
    | 'REVERT_TO_PRIOR_VERSION';
  id: string;
  channelId: string;
  commentId: number;
  userId: number;
  version: string;
};

let counter = 0;

async function fetchUnreadPosts(channelId: string | number) {
  const piniaPosts = useMemberPostStore();
  await piniaPosts.memberPostsSync({
    channelId,
    pageSize: 0,
    sort: '',
    filter: '',
    facets: [],
    search: '',
    unreadPosts: true,
    page: 0,
  });
}

async function requiredActionMemberPosts(channelId: string) {
  counter++;
  const piniaPosts = useMemberPostStore();

  if (counter > 1) {
    return;
  }
  await piniaPosts.memberPostsSync({
    channelId,
    pageSize: 0,
    sort: '',
    filter: '',
    facets: [],
    search: '',
    page: 0,
    complianceActionPosts: true,
  });
}

const fetchNewResults = () => {
  const emitter = useNuxtApp().$emitter;
  emitter.$emit('reload-cards', true);
};

const fetchComment = async (postId, commentId) => {
  const piniaComments = useCommentsStore();
  await piniaComments.getSingleComment({ postId, commentId });
};

const removePostComment = (commentId) => {
  const piniaComments = useCommentsStore();
  piniaComments.removeComment(commentId);
};

const fetchSinglePost = _.debounce(async (postId) => {
  // counter++;
  // if (counter > 1) {
  //   return;
  // }
  const piniaPosts = useMemberPostStore();
  try {
    await piniaPosts.getSinglePostSummary({
      postId,
    });
  } catch (error) {
    handleErrorAndNotificationHandler('Error fetching post summary:', error);
  }
}, 200);
const fetchServiceItemDetail = async (serviceItemId) => {
  const piniaPost = useMemberPostStore();
  await piniaPost.getServiceItemDetails(serviceItemId);
};
const contextUser = (userId: number) => {
  const piniaUser = useUserStore();
  const ctxId = piniaUser.user as unknown as {
    summary: { basicUserInfo: { userId: number } };
  };
  return ctxId.summary.basicUserInfo.userId === userId;
};

/**
 * Handles the UPDATE_POST pubsub event.
 *
 * Handles the ADD_DRAFT, DELETE_DRAFT, ADD_COMMENT, DELETE_COMMENT, EDIT_COMMENT,
 * EDIT_COMPLIANCE, EDIT_SHARING, EDIT_CLASSIFICATION, COMPLETED_COMPLIANCE_ACTION, REVERT_TO_PRIOR_VERSION.
 *
 * SIDE EFFECTS: Fetches the post summary and emits a global message.
 *
 * @param {PayloadDataType} PayloadDataType - The channel and data
 *
 * @returns {Promise<void>}
 */
export async function handleUpdatePost(
  ...[_channel, data]: PayloadDataType<UpdatePost>
) {
  if (DEBUG) console.log('handlePusherMessages', data);
  const piniaMemberPosts = useMemberPostStore();
  const emitter = useNuxtApp().$emitter;
  // const piniaTeams = useTeamsStore();

  const eventType = data.eventType;

  if (eventType === 'CREATE_COMMENT') {
    await fetchComment(data.id, data.commentId);
    if (contextUser(data.userId)) {
      emitter.$emit(GLOBAL_MESSAGE_EVENT, {
        message: 'New comment added to post.',
        status: 200,
      });
    }
  }
  if (eventType === 'DELETE_COMMENT') {
    removePostComment(data.commentId);
    if (contextUser(data.userId)) {
      emitter.$emit(GLOBAL_MESSAGE_EVENT, {
        message: 'Comment removed from post.',
        status: 200,
      });
    }
  }
  if (eventType === 'COMPLETED_COMPLIANCE_ACTION') {
    await requiredActionMemberPosts(data.channelId);
    if (!contextUser(data.userId)) {
      emitter.$emit('refetch-post');
    } else if (contextUser(data.userId)) {
      emitter.$emit('ctx-user-fetch-post-detail');
    }
  }

  if (eventType.includes('EDIT')) {
    let message = '';

    if (eventType === 'EDIT_SHARING') {
      message = 'You have succesfuly updated permissions on this post.';
      await fetchUnreadPosts(data.channelId);
    }
    if (eventType === 'EDIT_COMPLIANCE') {
      console.log('triggered in eventType');

      // await requiredActionMemberPosts(data.channelId);
      message = 'You have succesfuly updated required actions on this post.';
    }
    if (eventType === 'EDIT_CLASSIFICATION') {
      message = 'You have succesfuly updated classifications on this post.';
    }
    if (eventType === 'EDIT_CLASSIFICATION') {
      message = 'You have succesfully updated classification';
    }
    fetchNewResults();
    if (contextUser(data.userId)) {
      await fetchSinglePost(data.id);
      await fetchServiceItemDetail(data.id);
    }

    piniaMemberPosts.resetFields();
    if (!contextUser(data.userId)) {
      emitter.$emit('refetch-post');
    } else if (contextUser(data.userId)) {
      emitter.$emit('ctx-user-fetch-post-detail');
    }

    if (contextUser(data.userId)) {
      // piniaTeams.supressToastMessageForTeam(true)
      emitter.$emit(GLOBAL_MESSAGE_EVENT, {
        message,
        status: 200,
      });
      setTimeout(() => {
        // piniaTeams.supressToastMessageForTeam(false)
      }, 1000);
    }
  }

  if (eventType === 'ADD_DRAFT') {
    await fetchSinglePost(data.id);
  }
  if (eventType === 'DELETE_DRAFT') {
    piniaMemberPosts.removePostFromFeed(data.id);
  }
  if (eventType === 'PUBLISH_POST' || eventType === 'REVERT_TO_PRIOR_VERSION') {
    emitter.$emit('ctx-user-fetch-post-detail', data.version);
  }
}
